import React, { useState } from 'react'
import { Col, Form, Input, InputNumber, Row } from 'antd'
import { useTranslation } from 'lib/i18n'
import { darkGrey } from 'lib/theme'

import CtaButton from 'components/CtaButton/CtaButton'
import ErrorMessage from 'components/ErrorMessage/ErrorMessage'
import Heading from 'components/Heading/Heading'
import PhoneInput from 'components/PhoneInput/PhoneInput'
import ProfileReferralBlock from 'components/ProfileReferralBlock/ProfileReferralBlock'
import SocialMediaButtons from 'components/SocialMediaButtons/SocialMediaButtons'
import { IconEnvelopeLight, IconUser } from 'components/UI/Icons'
import useCreateProfileApplication from 'hooks/useCreateProfileApplication'
import useProfileReferralCode from 'hooks/useProfileReferralCode'
import useRouter from 'hooks/useRouter'

function EnrollForm({ title = '', showFull = false }) {
  const { t } = useTranslation()
  const router = useRouter()

  const [form] = Form.useForm()
  const [done, setDone] = useState(false)
  const [focused, setFocused] = useState(showFull)

  const { code } = router.query

  const { profileReferralCode, loading, error } = useProfileReferralCode({
    code,
    skip: !code,
  })

  const [createProfileApplication, { loading: submitLoading }] =
    useCreateProfileApplication({
      onCompleted: (data) => {
        const errors = data?.market?.createProfileApplication?.errors
        if (errors.length > 0) {
          errors.forEach((createProfileApplicationError) => {
            form.setFields([
              {
                name: createProfileApplicationError.field,
                errors: [createProfileApplicationError.message],
              },
            ])
          })
        } else {
          setDone(true)
          window.scrollTo(0, 0)
        }
      },
    })

  if (error) return <ErrorMessage message={JSON.stringify(error)} />

  const onFinish = (inputPayload) => {
    const input = inputPayload
    if (profileReferralCode) {
      input.code = profileReferralCode?.code
    }
    input.profileApplicationSource = 'webEnroll'
    createProfileApplication(input)
  }

  const onFinishFailed = ({ errorFields }) => {
    form.scrollToField(errorFields[0].name)
  }

  const onFocus = () => {
    setFocused(true)
  }

  if (done) {
    return (
      <div data-cy="enrollFormSuccessText" style={{ textAlign: 'center' }}>
        <h2>
          {t(
            'EnrollForm.enrollmentDoneTitle',
            'Thanks for signing up for memmo.',
          )}
        </h2>
        <p>
          {t(
            'EnrollForm.enrollmentDone',
            'Thanks for signing up for memmo - we will get back to you shortly.',
          )}
        </p>
        <p>
          {t(
            'EnrollForm.enrollmentDoneSupport',
            'If you have any questions - feel free to reach out to us on support@memmo.me.',
          )}
        </p>
        <p>
          {t(
            'EnrollForm.enrollmentDoneSocialMedia',
            'In the meantime - please follow us in our social media channels!',
          )}
        </p>

        <SocialMediaButtons />
      </div>
    )
  }

  const validator = (_, value) => {
    if (!value.trim().length) return Promise.resolve()

    const hasNormalChars = value.trim().replace(/([^a-z]+)/i, '').length > 0

    if (hasNormalChars) {
      return Promise.resolve()
    }
    return Promise.reject(
      new Error(
        t(
          'EnrollForm.nameError',
          'Name cannot only contain special characters',
        ),
      ),
    )
  }

  return (
    <Form
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
    >
      <Row gutter={24} type="flex">
        <Col span={24}>
          {code ? (
            <ProfileReferralBlock
              profileReferralCode={profileReferralCode}
              loading={loading}
            />
          ) : (
            <div style={{ fontSize: 18, marginBottom: 30 }}>
              <Heading level="h2">
                <h2>{title}</h2>
              </Heading>
            </div>
          )}
        </Col>

        <Col span={12}>
          <Form.Item
            name="name"
            onFocus={onFocus}
            rules={[
              {
                required: true,
                message: t('EnrollForm.nameRequired', 'Name is required'),
              },
              { validator },
            ]}
            label={t('EnrollForm.nameLabel', 'Name')}
            data-cy="name"
          >
            <Input size="large" prefix={<IconUser color={darkGrey} />} />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name="email"
            onFocus={onFocus}
            label={t('EnrollForm.emailLabel', 'E-mail')}
            normalize={(value) => value.trim()}
            rules={[
              {
                required: true,
                message: t('EnrollForm.emailRequired', 'E-mail is required'),
              },
              {
                type: 'email',
                message: t(
                  'EnrollForm.emailInvalid',
                  'The input is not valid e-mail!',
                ),
              },
            ]}
            data-cy="email"
          >
            <Input
              size="large"
              prefix={<IconEnvelopeLight color={darkGrey} />}
            />
          </Form.Item>
        </Col>
      </Row>

      {focused && (
        <div>
          <Row gutter={24} type="flex">
            <Col span={12}>
              <Form.Item
                name="phone"
                label={t('EnrollForm.phoneLabel', 'Phone')}
                data-cy="phone"
              >
                <PhoneInput size="large" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="alias"
                label={t('EnrollForm.aliasLabel', 'Alias')}
                data-cy="alias"
              >
                <Input size="large" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="followers"
                label={t(
                  'EnrollForm.followersLabel',
                  'How many followers do you have in your main channel?',
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      'EnrollForm.followersRequired',
                      'Followers is required',
                    ),
                  },
                ]}
                data-cy="followers"
              >
                <InputNumber step={1000} precision={0} min={0} size="large" />
              </Form.Item>
            </Col>

            {!profileReferralCode && (
              <Col xs={24} sm={12}>
                <Form.Item
                  name="code"
                  label={t(
                    'EnrollForm.codeLabel',
                    'Do you have a referral code?',
                  )}
                  data-cy="code"
                >
                  <Input size="large" />
                </Form.Item>
              </Col>
            )}

            <Col span={12}>
              <Form.Item
                name="instagram"
                label={t('EnrollForm.instagramLabel', 'Instagram')}
                rules={[
                  {
                    max: 30,
                    message: t(
                      'EnrollForm.instagramMaxlength',
                      'Instagram username can not be longer than 30 characters',
                    ),
                  },
                ]}
                data-cy="instagram"
              >
                <Input prefix="@" size="large" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="youtube"
                label={t('EnrollForm.youtubeLabel', 'Youtube')}
                rules={[
                  {
                    max: 100,
                    message: t(
                      'EnrollForm.yotubeMaxlength',
                      'Youtube channel name can not be longer than 20 characters',
                    ),
                  },
                ]}
                data-cy="youtube"
              >
                <Input size="large" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="twitter"
                label={t('EnrollForm.twitterLabel', 'Twitter')}
                rules={[
                  {
                    max: 15,
                    message: t(
                      'EnrollForm.twitterMaxlength',
                      'Twitter username can not be longer than 15 characters',
                    ),
                  },
                ]}
                data-cy="twitter"
              >
                <Input prefix="@" size="large" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="tiktok"
                label={t('EnrollForm.tiktokLabel', 'TikTok')}
                rules={[
                  {
                    max: 24,
                    message: t(
                      'EnrollForm.tiktokMaxlength',
                      'Tiktok username can not be longer than 24 characters',
                    ),
                  },
                ]}
                data-cy="tiktok"
              >
                <Input prefix="@" size="large" />
              </Form.Item>
            </Col>
          </Row>
        </div>
      )}
      <CtaButton
        htmlType="submit"
        loading={submitLoading}
        block
        data-cy="submitButton"
        disabled={submitLoading}
      >
        {t('EnrollForm.ctaButton', 'Continue')}
      </CtaButton>
    </Form>
  )
}

export default EnrollForm
